import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import styles from './ImageLightbox.module.css';

interface Props {
  images: string[];
  openIndex: number | null;
  onClose: () => void;
  /** Base name for downloads. A numeric suffix is appended per image. */
  downloadFilename?: string;
}

const triggerDownload = (url: string, filename: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const ImageLightbox = ({
  images,
  openIndex,
  onClose,
  downloadFilename = 'image',
}: Props) => {
  const isOpen = openIndex !== null && images.length > 0;
  const [index, setIndex] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null) setIndex(openIndex);
  }, [openIndex]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, goPrev, goNext]);

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentUrl = images[index];
  const hasMany = images.length > 1;

  const handleDownload = () => {
    triggerDownload(currentUrl, `${downloadFilename}-${index + 1}.png`);
  };

  const lightbox = (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <button
        className={styles.closeBtn}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
        type="button"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      <button
        className={styles.downloadBtn}
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        aria-label="Download"
        type="button"
      >
        <FontAwesomeIcon icon={faDownload} />
        <span className={styles.downloadText}>Download</span>
      </button>

      {hasMany && (
        <>
          <button
            className={`${styles.navBtn} ${styles.navPrev}`}
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous image"
            type="button"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            className={`${styles.navBtn} ${styles.navNext}`}
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next image"
            type="button"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}

      <img
        src={currentUrl}
        alt=""
        className={styles.image}
        onClick={(e) => e.stopPropagation()}
      />

      {hasMany && (
        <div className={styles.counter} onClick={(e) => e.stopPropagation()}>
          {index + 1} / {images.length}
        </div>
      )}
    </div>
  );

  return createPortal(lightbox, document.body);
};
